import { useState, useEffect, useCallback } from 'react';

export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  
  // Força atualização completa
  const forceUpdate = useCallback(async () => {
    try {
      // Limpa todos os caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        console.log('[App] Caches limpos');
      }
      
      // Desregistra SW antigo e registra novo
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
          console.log('[App] SW desregistrado');
        }
      }
      
      // Recarrega forçando bypass do cache
      window.location.reload(true);
    } catch (error) {
      console.error('[App] Erro ao forçar atualização:', error);
      window.location.reload(true);
    }
  }, []);
  
  // Verifica atualizações
  const checkForUpdates = useCallback(async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.update();
        console.log('[App] Verificando atualizações...');
      } catch (error) {
        console.error('[App] Erro ao verificar:', error);
      }
    }
  }, []);
  
  // Aplica atualização suave
  const applyUpdate = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage('SKIP_WAITING');
    }
    window.location.reload();
  }, [registration]);
  
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    const handleMessage = (event) => {
      if (event.data?.type === 'SW_ACTIVATED' || event.data?.type === 'SW_UPDATED') {
        console.log('[App] SW atualizado:', event.data.version);
        setCurrentVersion(event.data.version);
        // Auto-reload quando SW ativa nova versão
        if (event.data?.type === 'SW_ACTIVATED') {
          window.location.reload();
        }
      }
      if (event.data?.type === 'VERSION') {
        setCurrentVersion(event.data.version);
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleMessage);
    
    // Monitora atualizações do SW
    navigator.serviceWorker.ready.then((reg) => {
      setRegistration(reg);
      
      if (reg.active) {
        reg.active.postMessage('GET_VERSION');
      }
      
      if (reg.waiting) {
        setUpdateAvailable(true);
      }
      
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setUpdateAvailable(true);
          }
        });
      });
    });
    
    // Verifica atualizações ao abrir/voltar para o app
    checkForUpdates();
    
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    // Verifica a cada 2 minutos
    const interval = setInterval(checkForUpdates, 2 * 60 * 1000);
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [checkForUpdates]);
  
  return {
    updateAvailable,
    currentVersion,
    checkForUpdates,
    applyUpdate,
    forceUpdate
  };
};

export default useAppUpdate;
